import { getActionError } from "../../../utils/actionFeedback";
import AcademicLevelSelect from "../../../Components/AcademicLevelSelect";
import { buildUserUpdatePayload } from "../../../utils/userPayload";
import React, { useContext, useEffect, useState } from "react";
import CryptoJS from "crypto-js";
import { IoArrowBackCircle } from "react-icons/io5";

import { DarkModeContext } from "../../../Context/DarkModeContext.jsx";
import axiosInstance from "../../../Context/axiosInstance.js";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

function EditProfilePage() {
  const { isDarkMode } = useContext(DarkModeContext);
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);
  const [originalUserInfo, setOriginalUserInfo] = useState(null); // State to store original user info
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const [successMessage, setSuccessMessage] = useState(""); // New state for success message

  const userId = localStorage.getItem("userId");

  const decryptData = (encryptedData) => {
    const bytes = CryptoJS.AES.decrypt(
      encryptedData,
      process.env.REACT_APP_SECRET_KEY,
    );
    return bytes.toString(CryptoJS.enc.Utf8);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      setError({});
      try {
        const response = await axiosInstance.get(
          `/users/${decryptData(userId)}`,
        );
        setUserInfo(response.data);
        setOriginalUserInfo(response.data); // Store original data
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prevInfo) => ({
      ...prevInfo,
      [name]: name === "academicLevelId" ? Number(value) : value,
    }));
  };

  const hasChanges = () => {
    return JSON.stringify(userInfo) !== JSON.stringify(originalUserInfo); // Check for changes
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError({});
    setSuccessMessage("");

    try {
      await axiosInstance.put(`/users/${decryptData(userId)}`, buildUserUpdatePayload(userInfo));
      toast.success("Profile updated successfully.");
      setSuccessMessage("Profile successfully updated!");
      setOriginalUserInfo(userInfo); // Update original info to current state
    } catch (err) {
      toast.error(getActionError(err, "Could not update your profile."));
      if (err.response) {
        const { data } = err.response;

        if (data.errors && Array.isArray(data.errors)) {
          const errorMessages = data.errors.map((error, index) => (
            <div key={index}>{`${error.reason}`}</div>
          ));
          setError(
            errorMessages ||
              "Failed to update profile or password. Please try again.",
          );
        } else {
          setError(
            data?.detail ||
              "Failed to update profile or password. Please try again.",
          );
        }
      } else {
        // Network error or no response
        setError("Network error. Please check your connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to determine input type based on field name
  const getInputType = (key) => {
    switch (key) {
      case "email":
        return "email";
      case "password":
        return "password";
      case "guardianPhoneNumber":
      case "phoneNumber":
        return "tel";
      default:
        return "text";
    }
  };

  return (
    <div
      className={`${
        isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-customBlue text-black"
      } flex items-center justify-center min-h-screen px-4 pb-8 pt-24 sm:px-6 lg:px-8`}
    >
      <div
        className={`animate-zoomIn ${
          isDarkMode ? "bg-gray-600 text-black" : "bg-white text-black"
        }  p-4 sm:p-6 lg:p-8 rounded-lg shadow-xl w-full max-w-4xl`}
      >
        <div
          onClick={() => navigate(-1)}
          className="cursor-pointer flex items-center mb-4 text-blue-500"
        >
          <IoArrowBackCircle className="mr-1" size={20} />
          <button>Back</button>
        </div>
        <h2 className="text-xl font-bold mb-4">Edit Profile</h2>
        {loading ? (
          <div className="flex justify-center items-center min-h-[510px]">
            {" "}
            {/* Set a min height */}
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
              {userInfo &&
                Object.keys(userInfo)
                  .filter((key) => key !== "id" && key !== "academicLevel")
                  .map((key) => (
                    <div className="mb-4" key={key}>
                      <label
                        htmlFor={key}
                        className="block text-sm font-medium"
                      >
                        {key === "academicLevelId" ? "Academic Level" : key.charAt(0).toUpperCase() +
                          key.slice(1).replace(/([A-Z])/g, " $1")}
                      </label>
                      {key === "academicLevelId" ? (
                        <AcademicLevelSelect id={key} name={key} value={userInfo[key]} onChange={handleChange} className="mt-1 p-2 w-full border rounded bg-white text-gray-900" />
                      ) : key === "gender" ? (
                        <select id={key} name={key} value={userInfo[key]} onChange={handleChange} className="mt-1 p-2 w-full border rounded bg-white text-gray-900">
                          <option value="Male">Male</option><option value="Female">Female</option>
                        </select>
                      ) : (
                        <input
                          type={getInputType(key)}
                          id={key}
                          name={key}
                          value={userInfo[key]}
                          onChange={handleChange}
                          className="mt-1 p-2 w-full border rounded"
                        />
                      )}
                    </div>
                  ))}
            </div>
            {error && (
              <div className="text-red-500">
                <p>
                  {typeof error === "string"
                    ? error
                    : Object.keys(error).length > 0 && (
                        <div className="text-red-500">
                          <p>{error}</p>
                        </div>
                      )}
                </p>
              </div>
            )}
            {successMessage && (
              <div className="flex justify-center text-green-500 mt-4">
                {successMessage}
              </div> // Display success message
            )}
            <div className="flex justify-center mt-4">
              <button
                type="submit"
                className={`px-4 py-2 rounded ${
                  loading || !hasChanges()
                    ? "bg-blue-500 text-white opacity-50 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
                disabled={loading || !hasChanges()}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default EditProfilePage;
